






















































































import {Component, Vue} from "vue-property-decorator";
import StoreLayout from "@/components/layouts/StoreLayout.vue";
import Inputfield from "@/components/partials/Inputfield.vue";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import {CustomerBalance, CustomerProfile, ResellerInformation} from "@/interfaces/entities/CustomerProfile";
import {EsimProfile} from "@/interfaces/entities/EsimProfile";
import {Balance} from "@/interfaces/entities/Balance";
import TableList from "@/components/partials/TableList.vue";
import {isValidShareToken, parseShareToken} from "@/util/shareToken";
import {isValidityExpiredBalance} from "@/util/balancePlanStatus";

@Component({
    components: {
        TableList,
        TDSSpinner,
        StoreLayout,
        Inputfield
    }
})
export default class ESimShopProfile extends Vue {
    private profileDetails: CustomerProfile | null = null;
    private availableEsim: EsimProfile | undefined = undefined;
    private resellerInformation: ResellerInformation | null = null;
    private showInfo: boolean = false;
    private tableColumnsRemainingBalance: object = {
        expiration: {label: this.$t("tableHeader.expirationDate") as string, width: "150px", align: "left"},
        remaining: {label: this.$t("tableHeader.remaining") as string, width: "150px", align: "left"},
        dailyBalance: {label: this.$t("tableHeader.dailyBalance") as string, width: "160px", align: "left"},
        package: {label: this.$t("tableHeader.dataPackage") as string},
        purchased: {label: this.$t("tableHeader.purchased") as string, width: "120px"}
    };
    private tableColumnsRemainingBalanceMobile: object = {
        expiration: {label: this.$t("tableHeader.expirationDate") as string, width: "175px", align: "left"},
        remaining: {label: this.$t("tableHeader.remaining") as string, align: "left"}
    }
    private tableColumnsDevices: object = {
        date: {label: this.$t("tableHeader.installationDate") as string, align: "left"},
        status: {label: this.$t("tableHeader.status") as string},
        link: {label: "", align: "right"}
    };
    private tableColumnsDevicesMobile: object = {
        date: {label: this.$t("tableHeader.installationDate") as string, width: "175px", align: "left"},
        status: {label: this.$t("tableHeader.status") as string, align: "left"}
    };

    get isMobile(): boolean {
        return this.$store.state.isMobile;
    }

    getTokenFromInstallationUrl(url: string): string | null {
        try {
            const match = url?.match(/token=([^&]+)/);
            return match ? match[1] : null;
        } catch {
            return null;
        }
    }

    created() {
        this.fetchCustomerData();
    }


    private async fetchCustomerData() {
        const token = this.$route.query.token ?? "";
        if (!this.isValidShareToken(token)) {
            this.$toastr.error( this.$t("esimInstallation.error.tokenInvalid") ).then(() =>location.reload());
        } else {
            try {
                this.profileDetails = await this.$store.dispatch("FETCH_CUSTOMER_DATA", {token, includeResellerInfo: true});
                this.resellerInformation = this.profileDetails?.resellerInformation ?? null;
                console.log("[ESIMInstallationPage] Fetched customer data: ", this.profileDetails);
                this.availableEsim = this.profileDetails?.relatedEsims.find((eSim: EsimProfile) => eSim.active && eSim.state.toLowerCase() === "released");
                this.showInfo = true;
            }catch (e) {
                this.$toastr.error( this.$t("esimInstallation.error.tokenInvalid") ).then(() =>location.reload());
            }
        }
    }

    private isValidShareToken(token: string | (string | null)[]): boolean {
        if (!isValidShareToken(token)) {
            console.log("[ESIMShopProfilePage] Share token is missing, malformed or expired!");
            return false;
        }
        const payload = parseShareToken(token as string);
        const expiresAt = payload?.exp;
        console.log(
            `[ESIMShopProfilePage] Data from token:\n\tICCID='${payload?.sub}'\n\texpiresAt=` +
            `'${expiresAt ? new Date(expiresAt * 1000) : "never"}'`
        );
        return true;
    }

    private navigateToEsimInstall(url: string) {
        const token = this.getTokenFromInstallationUrl(url);
        if (token) {
            this.$router.push({
                path: "/esim/profile",
                query: {
                    token: token,
                    custom: "true"
                }
            });
        }
    }

    private getDailyBalance(balance: Balance): string {
        const remainingVolume = balance.extendedDataPolicy?.remainingHighSpeedVolume;
        return remainingVolume ? `${remainingVolume.sizeValue} ${remainingVolume.sizeUnit}` : "NA";
    }

    /**
     * The customer profile endpoint returns expired balances alongside live ones. This table is
     * headed "Remaining balance" and its rows print availableBalance, while the total above it
     * comes from totalAvailableBalance, which the server filters to live plans - so an expired
     * row would sit directly under a header that contradicts it. Filtering here keeps the page
     * showing what it shows today, whether or not the API filters.
     */
    get liveBalances(): Array<CustomerBalance> {
        return (this.profileDetails?.balances ?? []).filter(
            (balance: CustomerBalance) => !isValidityExpiredBalance(balance)
        );
    }

    private getRemaining(balance: Balance): string {
        if (balance.extendedDataPolicy && balance.extendedDataPolicy.type?.toLowerCase() === "unlimited") {
            return "Not Applicable";
        }
        return `${balance.availableBalance.sizeValue} ${balance.availableBalance.sizeUnit}`;
    }
}
